import React, { useState, useEffect } from 'react';
import { adminApi } from '../services/api';
import AuditSummary from './AuditSummary';
import '../assets/AuditTrail.css';
import '../assets/Pagination.css';

const AuditTrail = () => {
  // Tab state
  const [activeView, setActiveView] = useState('logs');

  // State for audit logs
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [limit] = useState(20);

  // Filter state - now arrays for multi-select
  const [filters, setFilters] = useState({
    recipientSearch: '',
    actions: [],
    invoiceTypes: [],
    months: [],
    years: [],
    startDate: '',
    endDate: ''
  });
  
  // Dropdown open states
  const [openDropdown, setOpenDropdown] = useState(null);

  // Applied filters (for actual API calls)
  const [appliedFilters, setAppliedFilters] = useState({});

  // Load audit logs on mount and when filters/page change
  useEffect(() => {
    fetchAuditLogs();
  }, [currentPage, appliedFilters]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown && !event.target.closest('.multi-select-dropdown')) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdown]);

  const fetchAuditLogs = async () => {
    setLoading(true);
    setError('');

    try {
      const params = {
        page: currentPage,
        limit,
        ...appliedFilters
      };

      const response = await adminApi.getInvoiceAuditTrail(params);
      setAuditLogs(response.data.auditLogs);
      setTotalPages(response.data.pagination.pages);
      setTotalRecords(response.data.pagination.total);
    } catch (err) {
      console.error('Failed to fetch audit logs:', err);
      setError(
        err.response?.data?.message || 
        'Failed to load audit trail. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  const handleFilterToggle = (field, value) => {
    const currentValues = filters[field];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    
    setFilters({ ...filters, [field]: newValues });
  };
  
  const toggleDropdown = (dropdown) => {
    setOpenDropdown(openDropdown === dropdown ? null : dropdown);
  };

  const handleApplyFilters = () => {
    // Build filter object for API
    const apiFilters = {};
    
    if (filters.actions.length > 0) {
      filters.actions.forEach(action => apiFilters.action = action); // API might need adjustment for multiple
    }
    if (filters.invoiceTypes.length > 0) {
      filters.invoiceTypes.forEach(type => apiFilters.invoiceType = type); // API might need adjustment for multiple
    }
    if (filters.months.length > 0) {
      filters.months.forEach(month => apiFilters.month = parseInt(month)); // API might need adjustment for multiple
    }
    if (filters.years.length > 0) {
      filters.years.forEach(year => apiFilters.year = parseInt(year)); // API might need adjustment for multiple
    }
    if (filters.startDate) apiFilters.startDate = filters.startDate;
    if (filters.endDate) apiFilters.endDate = filters.endDate;

    setAppliedFilters(apiFilters);
    setCurrentPage(1); // Reset to first page when applying filters
  };

  const handleClearFilters = () => {
    setFilters({
      recipientSearch: '',
      actions: [],
      invoiceTypes: [],
      months: [],
      years: [],
      startDate: '',
      endDate: ''
    });
    setAppliedFilters({});
    setCurrentPage(1);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-SG', {
      timeZone: 'Asia/Singapore',
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return `$${amount.toFixed(2)}`;
  };

  const getActionBadgeClass = (action) => {
    switch (action) {
      case 'generated':
        return 'badge-generated';
      case 'exported':
        return 'badge-exported';
      case 'emailed':
        return 'badge-emailed';
      default:
        return 'badge-default';
    }
  };

  const getEmailStatusBadgeClass = (status) => {
    switch (status) {
      case 'sent':
        return 'badge-success';
      case 'failed':
        return 'badge-error';
      case 'pending':
        return 'badge-pending';
      default:
        return 'badge-default';
    }
  };

  const months = [
    { value: '', label: 'All Months' },
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  const currentYear = new Date().getFullYear();
  const years = [
    { value: '', label: 'All Years' },
    ...Array.from({ length: currentYear - 2019 + 2 }, (_, i) => ({
      value: 2019 + i,
      label: (2019 + i).toString()
    }))
  ];

  // Filter audit logs by recipient search (client-side)
  const filteredAuditLogs = auditLogs.filter(log => {
    if (!filters.recipientSearch) return true;
    const searchLower = filters.recipientSearch.toLowerCase();
    return (
      log.recipientName?.toLowerCase().includes(searchLower) ||
      log.recipientEmail?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="audit-trail">
      <div className="audit-trail-header">
        <h2>Invoice Audit Trail</h2>
        <p className="audit-trail-description">
          Track all invoice generation, export, and email activities
        </p>
      </div>

      {/* View Toggle */}
      <div className="view-toggle">
        <button
          className={activeView === 'summary' ? 'active' : ''}
          onClick={() => setActiveView('summary')}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/>
          </svg>
          Summary Dashboard
        </button>
        <button
          className={activeView === 'logs' ? 'active' : ''}
          onClick={() => setActiveView('logs')}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 4h14v-2H7v2zm0 4h14v-2H7v2zM7 7v2h14V7H7z"/>
          </svg>
          Audit Logs
        </button>
      </div>

      {/* Summary View */}
      {activeView === 'summary' && (
        <AuditSummary />
      )}

      {/* Logs View */}
      {activeView === 'logs' && (
        <div className="audit-logs-view">

      {/* Filters Section */}
      <div className="audit-filters">
        <div className="filter-row">
          <div className="filter-group">
            <label>Recipient:</label>
            <input
              type="text"
              placeholder="Search by name or email..."
              value={filters.recipientSearch}
              onChange={(e) => handleFilterChange('recipientSearch', e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>Action ({filters.actions.length} selected):</label>
            <div className="multi-select-dropdown">
              <button 
                className="dropdown-toggle"
                onClick={() => toggleDropdown('actions')}
              >
                {filters.actions.length === 0 ? 'All Actions' : `${filters.actions.length} selected`}
                <span className="dropdown-arrow">{openDropdown === 'actions' ? '▲' : '▼'}</span>
              </button>
              {openDropdown === 'actions' && (
                <div className="dropdown-menu">
                  <label className="dropdown-item">
                    <input
                      type="checkbox"
                      checked={filters.actions.includes('generated')}
                      onChange={() => handleFilterToggle('actions', 'generated')}
                    />
                    <span>Generated</span>
                  </label>
                  <label className="dropdown-item">
                    <input
                      type="checkbox"
                      checked={filters.actions.includes('exported')}
                      onChange={() => handleFilterToggle('actions', 'exported')}
                    />
                    <span>Exported</span>
                  </label>
                  <label className="dropdown-item">
                    <input
                      type="checkbox"
                      checked={filters.actions.includes('emailed')}
                      onChange={() => handleFilterToggle('actions', 'emailed')}
                    />
                    <span>Emailed</span>
                  </label>
                </div>
              )}
            </div>
          </div>

          <div className="filter-group">
            <label>Invoice Type ({filters.invoiceTypes.length} selected):</label>
            <div className="multi-select-dropdown">
              <button 
                className="dropdown-toggle"
                onClick={() => toggleDropdown('invoiceTypes')}
              >
                {filters.invoiceTypes.length === 0 ? 'All Types' : `${filters.invoiceTypes.length} selected`}
                <span className="dropdown-arrow">{openDropdown === 'invoiceTypes' ? '▲' : '▼'}</span>
              </button>
              {openDropdown === 'invoiceTypes' && (
                <div className="dropdown-menu">
                  <label className="dropdown-item">
                    <input
                      type="checkbox"
                      checked={filters.invoiceTypes.includes('tutor')}
                      onChange={() => handleFilterToggle('invoiceTypes', 'tutor')}
                    />
                    <span>Tutor</span>
                  </label>
                  <label className="dropdown-item">
                    <input
                      type="checkbox"
                      checked={filters.invoiceTypes.includes('tutee')}
                      onChange={() => handleFilterToggle('invoiceTypes', 'tutee')}
                    />
                    <span>Tutee</span>
                  </label>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="filter-row">
          <div className="filter-group">
            <label>Month ({filters.months.length} selected):</label>
            <div className="multi-select-dropdown">
              <button 
                className="dropdown-toggle"
                onClick={() => toggleDropdown('months')}
              >
                {filters.months.length === 0 ? 'All Months' : `${filters.months.length} selected`}
                <span className="dropdown-arrow">{openDropdown === 'months' ? '▲' : '▼'}</span>
              </button>
              {openDropdown === 'months' && (
                <div className="dropdown-menu">
                  {months.filter(m => m.value !== '').map(month => (
                    <label key={month.value} className="dropdown-item">
                      <input
                        type="checkbox"
                        checked={filters.months.includes(month.value)}
                        onChange={() => handleFilterToggle('months', month.value)}
                      />
                      <span>{month.label}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="filter-group">
            <label>Year ({filters.years.length} selected):</label>
            <div className="multi-select-dropdown">
              <button 
                className="dropdown-toggle"
                onClick={() => toggleDropdown('years')}
              >
                {filters.years.length === 0 ? 'All Years' : `${filters.years.length} selected`}
                <span className="dropdown-arrow">{openDropdown === 'years' ? '▲' : '▼'}</span>
              </button>
              {openDropdown === 'years' && (
                <div className="dropdown-menu">
                  {years.filter(y => y.value !== '').map(year => (
                    <label key={year.value} className="dropdown-item">
                      <input
                        type="checkbox"
                        checked={filters.years.includes(year.value)}
                        onChange={() => handleFilterToggle('years', year.value)}
                      />
                      <span>{year.label}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="filter-group">
            <label>Start Date:</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>End Date:</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
            />
          </div>
        </div>

        <div className="filter-actions">
          <button className="apply-filters-btn" onClick={handleApplyFilters}>
            Apply Filters
          </button>
          <button className="clear-filters-btn" onClick={handleClearFilters}>
            Clear All Filters
          </button>
        </div>
      </div>

      {/* Results Summary */}
      {!loading && !error && (
        <div className="results-summary">
          <p>
            Showing {filteredAuditLogs.length} of {totalRecords} records
            {Object.keys(appliedFilters).length > 0 && ' (filtered)'}
          </p>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading audit trail...</p>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="error-state">
          <p className="error-message">{error}</p>
          <button className="retry-btn" onClick={fetchAuditLogs}>
            Retry
          </button>
        </div>
      )}

      {/* Audit Logs Table */}
      {!loading && !error && filteredAuditLogs.length > 0 && (
        <div className="audit-table-container">
          <table className="audit-table">
            <thead>
              <tr>
                <th>Date & Time</th>
                <th>Action</th>
                <th>Invoice Type</th>
                <th>Recipient</th>
                <th>Period</th>
                <th>Amount</th>
                <th>Administrator</th>
                <th>Email Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredAuditLogs.map((log) => (
                <tr key={log._id}>
                  <td className="date-cell">
                    {formatDate(log.generatedAt)}
                  </td>
                  <td>
                    <span className={`badge ${getActionBadgeClass(log.action)}`}>
                      {log.action}
                    </span>
                  </td>
                  <td className="type-cell">
                    {log.invoiceType}
                  </td>
                  <td className="recipient-cell">
                    <div className="recipient-name">{log.recipientName}</div>
                    {log.recipientEmail && (
                      <div className="recipient-email">{log.recipientEmail}</div>
                    )}
                  </td>
                  <td className="period-cell">
                    {months.find(m => m.value === log.month)?.label} {log.year}
                  </td>
                  <td className="amount-cell">
                    {formatCurrency(log.totalAmount)}
                  </td>
                  <td className="admin-cell">
                    {log.generatedBy?.name || 'System'}
                  </td>
                  <td>
                    {log.action === 'emailed' ? (
                      <span className={`badge ${getEmailStatusBadgeClass(log.emailStatus)}`}>
                        {log.emailStatus}
                      </span>
                    ) : (
                      <span className="badge badge-default">N/A</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* No Results */}
      {!loading && !error && filteredAuditLogs.length === 0 && (
        <div className="no-results">
          <p>No audit logs found</p>
          {Object.keys(appliedFilters).length > 0 && (
            <button className="clear-filters-btn" onClick={handleClearFilters}>
              Clear Filters
            </button>
          )}
        </div>
      )}

      {/* Pagination */}
      {!loading && !error && totalPages > 1 && (
        <div className="pagination">
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          
          <div className="pagination-info">
            Page {currentPage} of {totalPages}
          </div>
          
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}
        </div>
      )}
    </div>
  );
};

export default AuditTrail;
